import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { bookVisit, listVisits, recordVisit } from '../lib/api';
import { dateTime, friendlyError, fromLocalInput, outcomeLabel, toLocalInput } from '../lib/format';
import type { Lead, Visit, VisitStatus } from '../lib/types';
import { Badge, Button, ErrorText, SelectField, TextField } from './ui';

const statusLabel: Record<VisitStatus, string> = { booked: 'Booked', done: 'Done', no_show: 'No-show', cancelled: 'Cancelled' };

export function VisitsPanel({ lead, onChanged }: { lead: Lead; onChanged: () => void }) {
  const [visits, setVisits] = useState<Visit[]>([]);
  const [booking, setBooking] = useState(false);
  const [recording, setRecording] = useState<Visit | null>(null);
  const load = useCallback(async () => setVisits(await listVisits(lead.id).catch(() => [])), [lead.id]);
  useEffect(() => { load(); }, [load]);
  const done = async () => { setBooking(false); setRecording(null); await load(); onChanged(); };

  return (
    <section className="border-b border-line px-5 py-4">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-muted">Site visits</h3>
        {!booking && <Button onClick={() => setBooking(true)}>Book a visit</Button>}
      </div>
      {visits.length === 0 && !booking && <p className="text-sm text-muted">No visits yet.</p>}
      <ul className="flex flex-col gap-2">
        {visits.map((v) => (
          <li key={v.id} className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <span>{dateTime(v.scheduled_at)} {v.outcome ? `· ${outcomeLabel[v.outcome]}` : ''}{v.outcome_note ? ` · ${v.outcome_note}` : ''}</span>
            <span className="flex items-center gap-2">
              <Badge tone={v.status === 'done' ? 'ok' : v.status === 'booked' ? 'accent' : 'bad'}>{statusLabel[v.status]}</Badge>
              {v.status === 'booked' && <Button onClick={() => setRecording(v)}>Record outcome</Button>}
            </span>
          </li>
        ))}
      </ul>
      {booking && <BookForm lead={lead} onDone={done} onCancel={() => setBooking(false)} />}
      {recording && <OutcomeForm visit={recording} onDone={done} onCancel={() => setRecording(null)} />}
    </section>
  );
}

function BookForm({ lead, onDone, onCancel }: { lead: Lead; onDone: () => void; onCancel: () => void }) {
  const tomorrow11 = (() => { const d = new Date(Date.now() + 86400e3); const v = toLocalInput(d); return `${v.slice(0, 10)}T11:00`; })();
  const [at, setAt] = useState(tomorrow11);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function submit(e: FormEvent) {
    e.preventDefault(); setBusy(true); setError('');
    try { await bookVisit(lead.id, fromLocalInput(at), note); onDone(); } catch (err) { setError(friendlyError(err)); setBusy(false); }
  }
  return (
    <form onSubmit={submit} className="mt-3 flex flex-col gap-3 rounded-xl bg-surface2 p-3">
      <TextField label="Visit time (IST)" type="datetime-local" required value={at} onChange={(e) => setAt(e.target.value)} />
      <TextField label="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Coming with parents; show tower C" />
      <ErrorText>{error}</ErrorText>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="submit" variant="primary" disabled={busy}>{busy ? 'Booking…' : 'Book visit'}</Button>
      </div>
    </form>
  );
}

function OutcomeForm({ visit, onDone, onCancel }: { visit: Visit; onDone: () => void; onCancel: () => void }) {
  const [status, setStatus] = useState<VisitStatus>('done');
  const [outcome, setOutcome] = useState('liked');
  const [note, setNote] = useState('');
  const [nextAt, setNextAt] = useState(toLocalInput(new Date(Date.now() + 86400e3)));
  const [nextNote, setNextNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function submit(e: FormEvent) {
    e.preventDefault(); setBusy(true); setError('');
    try {
      await recordVisit(visit.id, status, status === 'done' ? outcome : null, note, status === 'cancelled' && !nextNote ? null : fromLocalInput(nextAt), nextNote);
      onDone();
    } catch (err) { setError(friendlyError(err)); setBusy(false); }
  }
  return (
    <form onSubmit={submit} className="mt-3 flex flex-col gap-3 rounded-xl bg-surface2 p-3">
      <SelectField label="What happened" value={status} onChange={(e) => setStatus(e.target.value as VisitStatus)}>
        <option value="done">Visit done</option><option value="no_show">Buyer did not come</option><option value="cancelled">Cancelled</option>
      </SelectField>
      {status === 'done' && (
        <SelectField label="How did it go?" value={outcome} onChange={(e) => setOutcome(e.target.value)}>
          {Object.entries(outcomeLabel).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </SelectField>
      )}
      <TextField label="Note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Liked C-1402, worried about possession date" />
      <div className="grid gap-3 sm:grid-cols-2">
        <TextField label="Next step (IST)" type="datetime-local" required={status !== 'cancelled'} value={nextAt} onChange={(e) => setNextAt(e.target.value)} />
        <TextField label="Next step note" required={status !== 'cancelled'} value={nextNote} onChange={(e) => setNextNote(e.target.value)}
          placeholder={status === 'no_show' ? 'Rebook the visit' : 'Send cost sheet'} />
      </div>
      <ErrorText>{error}</ErrorText>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="submit" variant="primary" disabled={busy}>{busy ? 'Saving…' : 'Save outcome'}</Button>
      </div>
    </form>
  );
}
